<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { catalogApi } from '$lib/api/catalog';
  import { getErrorMessage } from '$lib/api/client';

  const {
    onOpenReview,
    onOpenStale,
    onOpenDuplicates
  }: {
    onOpenReview: () => void;
    onOpenStale: () => void;
    onOpenDuplicates: () => void;
  } = $props();

  // Shares the ['catalog-issues'] query key with CatalogPage's nav badge —
  // one fetch serves both.
  const issuesQuery = createQuery(() => ({
    queryKey: ['catalog-issues'],
    queryFn: () => catalogApi.issues()
  }));
</script>

{#if issuesQuery.isLoading}
  <p class="tym-loading-state">Loading issues&hellip;</p>
{:else if issuesQuery.isError}
  <p class="tym-error-state">{getErrorMessage(issuesQuery.error)}</p>
{:else if issuesQuery.data}
  {@const data = issuesQuery.data}
  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
    <button type="button" class="tym-card p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-[var(--tym-shadow-2)]" onclick={onOpenDuplicates}>
      <div class="flex items-center justify-between">
        <div class="font-heading text-[13.5px] font-bold text-navy">Duplicate products</div>
        <span class="tym-badge tym-badge-warning tabular-nums">
          {data.duplicate_groups.reduce((n, g) => n + g.products.length, 0)}
        </span>
      </div>
      <p class="tym-small mt-1">{data.duplicate_groups.length} group{data.duplicate_groups.length === 1 ? '' : 's'} sharing a normalized part number.</p>
    </button>

    <button type="button" class="tym-card p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-[var(--tym-shadow-2)]" onclick={onOpenReview}>
      <div class="flex items-center justify-between">
        <div class="font-heading text-[13.5px] font-bold text-navy">Needs cost price</div>
        <span class="tym-badge tym-badge-warning tabular-nums">{data.review_products.length}</span>
      </div>
      <p class="tym-small mt-1">Designer-created products still missing a cost price.</p>
    </button>

    <div class="tym-card p-4">
      <div class="flex items-center justify-between">
        <div class="font-heading text-[13.5px] font-bold text-navy">RCU taxonomy conflicts</div>
        <span class="tym-badge tym-badge-danger tabular-nums">{data.rcu_category_conflicts.length}</span>
      </div>
      <p class="tym-small mt-1 mb-2">Syncs to RCU but missing functional category/type — silently skipped downstream.</p>
      {#if data.rcu_category_conflicts.length > 0}
        <ul class="max-h-[180px] overflow-y-auto divide-y divide-toyama-cloudy-gray text-[12px]">
          {#each data.rcu_category_conflicts as p (p.id)}
            <li class="py-1.5">
              <span class="font-medium text-navy">{p.name}</span>
              <span class="text-dark-gray font-mono ml-1">{p.part_number}</span>
            </li>
          {/each}
        </ul>
      {/if}
    </div>

    <button type="button" class="tym-card p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-[var(--tym-shadow-2)]" onclick={onOpenStale}>
      <div class="flex items-center justify-between">
        <div class="font-heading text-[13.5px] font-bold text-navy">Stale products</div>
        <span class="tym-badge tym-badge-neutral tabular-nums">{data.stale_count}</span>
      </div>
      <p class="tym-small mt-1">No usage on any quotation created in the last 6 months. Not counted in the issues badge.</p>
    </button>
  </div>
{/if}
